import { useEffect, useCallback, useState, useRef } from 'react';

/**
 * Executes a callback ONCE when an element is enters the viewport.
 * If the element re-enters the viewport, the callback will NOT be executed again.
 */
export function useOnElementIntersection(
  onElementIntersectionCallback: () => void
) {
  const hasCallbackBeenCalledRef = useRef<boolean>(false);
  const [currentElement, setCurrentElement] = useState<HTMLElement | null>(
    null
  );

  const handleIntersection = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      const isElementInView = entry.isIntersecting;
      if (isElementInView && !hasCallbackBeenCalledRef.current) {
        onElementIntersectionCallback();
        hasCallbackBeenCalledRef.current = true;
      }
    },
    [onElementIntersectionCallback, hasCallbackBeenCalledRef]
  );

  useEffect(() => {
    if (!currentElement) {
      return;
    }

    const observer = new IntersectionObserver(handleIntersection);
    observer.observe(currentElement);

    return () => {
      observer.disconnect();
    };
  }, [currentElement, handleIntersection]);

  // https://18.react.dev/reference/react-dom/components/common#ref-callback
  const refCallback = useCallback((node: HTMLElement | null) => {
    setCurrentElement(node);
  }, []);

  return refCallback;
}
